import type { ReactNode } from "react";
import type { EnvironmentView, ScenePlan } from "@pushback-and-chaos/contracts";
import { SiteHeader } from "../ui/site-header.js";
import { EnvironmentSection } from "../environment/section.js";
import type { AuthMode, RouteName } from "../routes.js";

/**
 * The chrome around every screen.
 *
 * THE SITE HEADER (2026-09-22) is the one thing every route now shares: a
 * slim row with the mark, the wordmark and the collapsed menu, above whatever
 * the route draws. It replaces the menu that each screen used to carry in its
 * own chrome, and it takes its height out of the viewport rather than pushing
 * a composition off the screen — `.pc-under-header` is how the Storyteller
 * surfaces learn that they have `--pc-header-h` less to fill.
 *
 * Below the header, `band` still decides what a route gets:
 *
 *   "home" / "play" — NOTHING ELSE. Both screens are one viewport-contained
 *     composition (the front door in the Storyteller's vocabulary, 2026-09-20;
 *     the Storyteller layout, 2026-09-17).
 *   "plain" — the paper ground and the page.
 *   "grid" — the older band: the animated architectural section of the
 *     consulting floor (ADR 0009) when the server has derived an
 *     EnvironmentView, else the grid paper it stood in for. A dev-only escape
 *     hatch restores the grid paper (localStorage "pc-dev-grid-band" = "1").
 */
export function AppShell({
  children,
  route,
  onNavigate,
  band,
  environment = null,
  environmentPlan = null,
  onLogOut,
  onGuestAuth,
}: {
  children: ReactNode;
  route: RouteName;
  onNavigate: (r: RouteName) => void;
  band: "grid" | "plain" | "home" | "play";
  environment?: EnvironmentView | null;
  /** The turn's transient ScenePlan (ADR 0014), played once by the section. */
  environmentPlan?: ScenePlan | null;
  /** Present only for a signed-in account; see SiteHeader. */
  onLogOut?: (() => void) | undefined;
  /** Present only for a guest; see SiteHeader. */
  onGuestAuth?: ((mode: AuthMode) => void) | undefined;
}): ReactNode {
  const header = <SiteHeader onNavigate={onNavigate} current={route} onLogOut={onLogOut} onGuestAuth={onGuestAuth} />;

  if (band === "play" || band === "home") {
    return (
      <div className="pc-under-header">
        {header}
        {children}
      </div>
    );
  }

  if (band === "plain") {
    return (
      <div className="pc-under-header min-h-svh bg-paper">
        {header}
        {children}
      </div>
    );
  }

  const forceGridPaper = typeof localStorage !== "undefined" && localStorage.getItem("pc-dev-grid-band") === "1";
  const showSection = environment !== null && !forceGridPaper;

  return (
    <div className="pc-under-header min-h-svh bg-white">
      {header}
      <div
        aria-label={showSection ? undefined : "Reserved for interface not yet designed"}
        className={showSection ? "relative w-full" : "pc-gridpaper relative w-full"}
        style={{ height: "var(--pc-grid-band)" }}
      >
        {showSection ? <EnvironmentSection view={environment} plan={environmentPlan} /> : null}
      </div>
      {children}
    </div>
  );
}
